import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Clock, Loader2, PenLine, UserRound, X } from 'lucide-react';
import {
    FOREIGN_REASON_MAX,
    FOREIGN_REASON_MIN,
    foreignReasonLength,
    validateForeignReason,
} from '../services/foreignAccess';
import {
    FOREIGN_DIALOG_INFO,
    FOREIGN_JUSTIFY_LABEL,
    FOREIGN_VIEW_ONLY_LABEL,
} from '../services/readOnlyNotice';

/**
 * Begruendung fuer die Arbeit in einem fremden Projekt (SQL Nr. 128).
 *
 * Die Komponente sammelt nur den Text ein und zeigt, was App.tsx ihr sagt
 * (busy, error). Freigabe, Sperre und Neuladen stehen in App.tsx — dort liegen
 * Sperrzustand und Bericht, und nur dort laesst sich der Wechsel in den
 * Schreibmodus in der richtigen Reihenfolge machen.
 *
 * Bedienung, bewusst:
 *   * Enter schickt NICHT ab. Die Begruendung ist Freitext, ein Zeilenumbruch
 *     ist dort eine Eingabe und kein Befehl — ein halber Satz ginge sonst mit
 *     Benachrichtigung an Eigentuemer und Verantwortliche raus.
 *   * Esc und Klick daneben heissen „Nur ansehen". Das Projekt bleibt offen,
 *     nur eben ohne Schreibrecht; verloren geht dabei nichts.
 *   * Waehrend der Server arbeitet, laesst sich nichts abbrechen: die Freigabe
 *     koennte gerade entstehen, und ein „abgebrochen" waere dann gelogen.
 *
 * Am Handy von unten (Bottom-Sheet), am Rechner mittig. Portal an den body:
 * die Projektansichten haben overflow-hidden-Spalten, in denen ein fixiertes
 * Element sonst abgeschnitten werden kann.
 */
interface Props {
    projectNumber?: string | null;
    projectName?: string | null;
    /** Hauptverantwortlicher laut Projekt — wer benachrichtigt wird. */
    ownerName?: string | null;
    /** Freigabe, Sperre und Neuladen laufen. */
    busy: boolean;
    /** Meldung des Servers bzw. von App.tsx; null = nichts. */
    error: string | null;
    onSubmit: (reason: string) => void;
    onCancel: () => void;
}

export const ForeignAccessDialog: React.FC<Props> = ({
    projectNumber, projectName, ownerName, busy, error, onSubmit, onCancel,
}) => {
    const [reason, setReason] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const titleId = useId();
    const hintId = useId();

    const length = foreignReasonLength(reason);
    const problem = validateForeignReason(reason);
    const canSubmit = problem === null && !busy;

    // Der Fokus gehoert sofort ins Textfeld — der Dialog ist genau eine Frage.
    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    // Esc = „Nur ansehen". Am document, damit es auch greift, wenn der Fokus
    // gerade auf einem Knopf liegt. Waehrend des Arbeitens gesperrt (s. oben).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || busy) return;
            e.preventDefault();
            onCancel();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [busy, onCancel]);

    const handleSubmit = () => {
        if (!canSubmit) return;
        onSubmit(reason);
    };

    // Die Pruefmeldung erst zeigen, wenn jemand angefangen hat zu tippen — ein
    // leeres Feld mit Fehlermeldung begruesst niemanden. Bis dahin steht die
    // Regel als neutraler Hinweis da, und der Hauptknopf bleibt gesperrt.
    const showProblem = problem !== null && length > 0;
    const projektZeile = [projectNumber ? `Nr. ${projectNumber}` : '', projectName || ''].filter(Boolean).join(' · ');

    return createPortal((
        <div
            className="fixed inset-0 z-[300] bg-slate-900/50 flex items-end sm:items-center justify-center sm:p-4 animate-fade-in"
            style={{ height: '100dvh' }}
            onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-slide-up"
                style={{ maxHeight: 'calc(100dvh - env(safe-area-inset-top))' }}
            >
                {/* Griff — am Handy das Zeichen, dass die Flaeche von unten kommt. */}
                <div className="sm:hidden pt-2.5 pb-1 flex justify-center shrink-0"><div className="w-10 h-1.5 rounded-full bg-slate-200" /></div>

                <div className="px-5 pt-3 sm:pt-5 pb-4 border-b border-slate-100 shrink-0 flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className="bg-amber-50 text-amber-700 p-2 rounded-xl border border-amber-200 shrink-0">
                            <PenLine className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                            <h3 id={titleId} className="font-semibold text-slate-800 leading-tight">Fremdes Projekt bearbeiten</h3>
                            {projektZeile && <p className="text-xs text-slate-600 mt-0.5 break-words">{projektZeile}</p>}
                            {ownerName && (
                                <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
                                    <UserRound className="w-3 h-3 shrink-0" />
                                    <span className="truncate">Verantwortlich: {ownerName}</span>
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        aria-label={FOREIGN_VIEW_ONLY_LABEL}
                        className="p-2 -mr-1 -mt-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors disabled:opacity-40 shrink-0"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-5 py-4 space-y-3 overflow-y-auto">
                    <p className="text-sm text-slate-600 leading-relaxed">
                        Sie sind in diesem Projekt nicht eingetragen. Für eine Begehung oder Buchung bitte kurz festhalten, warum Sie hier arbeiten.
                    </p>

                    <div className="space-y-1">
                        <label htmlFor={`${titleId}-reason`} className="text-[11px] uppercase font-semibold text-slate-500 ml-1 block">
                            Begründung *
                        </label>
                        <textarea
                            id={`${titleId}-reason`}
                            ref={textareaRef}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={4}
                            // Die Browsergrenze zaehlt UTF-16-Einheiten und greift
                            // damit nie spaeter als die Zeichengrenze der Pruefung.
                            maxLength={FOREIGN_REASON_MAX}
                            // readOnly statt disabled: der Fokus bleibt im Feld und
                            // steht nach einer Fehlermeldung sofort wieder bereit.
                            readOnly={busy}
                            aria-invalid={showProblem}
                            aria-describedby={hintId}
                            placeholder="z. B. Vertretung für Frau Müller, Begehung Haus B"
                            className={
                                'w-full px-3 py-2 border rounded-xl text-sm text-slate-800 outline-none transition-colors resize-y min-h-[6rem] ' +
                                'read-only:bg-slate-50 read-only:text-slate-500 ' +
                                (showProblem
                                    ? 'border-amber-400 focus:ring-2 focus:ring-amber-200'
                                    : 'border-slate-300 focus:ring-2 focus:ring-blue-200 focus:border-blue-400')
                            }
                        />
                        <div id={hintId} className="flex items-start justify-between gap-3 px-1">
                            <span className={`text-[11px] ${showProblem ? 'text-amber-700 font-semibold' : 'text-slate-500'}`}>
                                {showProblem ? problem : `Mindestens ${FOREIGN_REASON_MIN} Zeichen.`}
                            </span>
                            <span
                                className={`text-[11px] tabular-nums shrink-0 ${length > FOREIGN_REASON_MAX ? 'text-amber-700 font-semibold' : 'text-slate-400'}`}
                            >
                                {length.toLocaleString('de-DE')} / {FOREIGN_REASON_MAX.toLocaleString('de-DE')}
                            </span>
                        </div>
                    </div>

                    <p className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                        <Clock className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-500" />
                        <span>{FOREIGN_DIALOG_INFO}</span>
                    </p>

                    {error && (
                        <p role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 whitespace-pre-line">
                            {error}
                        </p>
                    )}
                </div>

                <div
                    className="px-5 py-3 border-t border-slate-100 bg-white shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
                    style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
                >
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="py-3 sm:py-2 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {FOREIGN_VIEW_ONLY_LABEL}
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="py-3 sm:py-2 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                        <span>{FOREIGN_JUSTIFY_LABEL}</span>
                    </button>
                </div>
            </div>
        </div>
    ), document.body);
};
